import {useEffect, useRef, useState} from "react";
import {HexColorPicker} from "react-colorful";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";

const normalizedHex = (value: string) => {
    const hex = value.trim().replace(/^#/, "");
    return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex.toLowerCase()}` : undefined;
};

export default function LightColorControl({color, onPreview, onCommit, compact = false, disabled = false}: {
    color: string;
    onPreview: (color: string) => void;
    onCommit: (color: string) => void;
    compact?: boolean;
    disabled?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [hexInput, setHexInput] = useState(color);
    const pending = useRef<string | undefined>(undefined);
    const commit = () => {
        const value = pending.current;
        pending.current = undefined;
        if (value) onCommit(value);
    };
    useEffect(() => {
        if (!open) return;
        const onInteractionEnd = () => commit();
        window.addEventListener("pointerup", onInteractionEnd);
        window.addEventListener("mouseup", onInteractionEnd);
        window.addEventListener("touchend", onInteractionEnd);
        return () => {
            window.removeEventListener("pointerup", onInteractionEnd);
            window.removeEventListener("mouseup", onInteractionEnd);
            window.removeEventListener("touchend", onInteractionEnd);
        };
    });
    const preview = (value: string) => {
        pending.current = value;
        setHexInput(value);
        onPreview(value);
    };
    const commitInput = () => {
        const value = normalizedHex(hexInput);
        if (value) {
            pending.current = value;
            commit();
            setHexInput(value);
        } else {
            pending.current = undefined;
            setHexInput(color);
            onPreview(color);
        }
    };
    return <div className={compact ? "flex items-center justify-between text-xs" : "flex items-center justify-between text-sm"}>
        <span>Farbe</span>
        <Popover open={open && !disabled} onOpenChange={next => {
            if (disabled) return;
            if (next) setHexInput(color);
            else commit();
            setOpen(next);
        }}>
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" size="sm" className={compact ? "h-7 w-24 text-xs" : "w-24"}
                    disabled={disabled} style={{backgroundColor: color}}>{color.toUpperCase()}</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-fit space-y-2 rounded-xl p-2"
                onKeyUp={event => {if (event.key.startsWith("Arrow")) commit();}}>
                <HexColorPicker color={color} onChange={preview}/>
                <Input aria-label="Hex-Farbwert" value={hexInput} maxLength={7}
                    onChange={event => {
                        const value = event.target.value;
                        setHexInput(value);
                        const parsed = normalizedHex(value);
                        if (parsed) {pending.current = parsed; onPreview(parsed);}
                        else pending.current = undefined;
                    }}
                    onBlur={commitInput}
                    onKeyDown={event => {if (event.key === "Enter") {commitInput(); event.currentTarget.blur();}}}/>
            </PopoverContent>
        </Popover>
    </div>;
}
